/*
 * AudioTrack - An individual audio file.
 * AudioSection - A grouping of AudioTrack's that are played concurrently. 
 * AudioSequence - A sequence of AudioSection's to be played sequentially.  
 */
import { Howl, Howler } from 'howler';
Howler.usingWebAudio = true;
Howler.mute(false); // to initialize Howler internals

// Howlers automatic unlock only occurs if we are using web audio.
// Here we've implemented our own.
document.addEventListener('touchstart', autoUnlock, true);
document.addEventListener('touchend', autoUnlock, true);
document.addEventListener('click', autoUnlock, true);
document.addEventListener('keydown', autoUnlock, true);

async function autoUnlock() {
    await window.audioPlayer.unlock();
    if (await window.audioPlayer.hasPlaybackPermissions()) {
        document.removeEventListener('touchstart', autoUnlock, true);
        document.removeEventListener('touchend', autoUnlock, true);
        document.removeEventListener('click', autoUnlock, true);
        document.removeEventListener('keydown', autoUnlock, true);
    }
}

/*
 * EndMode: An Audio Section can have looping tracks. The end mode defines when the playback should be considered ended.
 *   PrimaryTrackEnds - Once the primary track has finished playback.
 *   LastTrackEnds - Once the last non-looping track has finished playback. 
 *   ExplicitStopRequest - Once .stop is called on the playback. If there is no looping track, the section will be finished once last track ends.
 */
export const EndMode = {
    PrimaryTrackEnds: 0,
    LastTrackEnds: 1,
    ExplicitStopRequest: 2
} as const;
export type EndModeType = typeof EndMode[keyof typeof EndMode];

type AudioTrackOptions = {
    src: string | null;
    volume?: number;
    loop?: boolean;
    offset?: number;
    seekTo?: number;
    duration?: number;
};

type AudioSectionOptions = {
    primaryTrack?: AudioTrackOptions;
    secondaryTracks?: AudioTrackOptions[];
    endMode?: EndModeType;
    events?: {
        onPlaybackEnded?: () => void;
        onLastTrackEnded?: () => void;
        onPrimaryTrackEnded?: () => void;
        onPlaybackStarted?: () => void;
    };
};

type AudioSequenceOptions = AudioSectionOptions[];

/*
 * AudioTrack - An individual audio file.
 */
interface IAudioTrack {
    play(): Promise<void>;
    stop(): void;
    pause(): void;
    resume(): void;
    isLoop: boolean;
    isPrimary: boolean;
    isPlaybackEnded: boolean;
    onPlaybackEnded: (track: IAudioTrack) => void;
}

/*
 * AudioSection - A grouping of AudioTrack's that are played concurrently. 
 */
interface IAudioSection {
    play(): Promise<void>;
    stop(): void;
    pause(): void;
    resume(): void;
}

/*
 * AudioSequence - A sequence of AudioSection's to be played sequentially.  
 */
interface IAudioSequence {
    play(audioSequenceOptions: AudioSequenceOptions): Promise<void>;
    stop(): void;
    pause(): void;
    resume(): void;
}

declare global {
    interface Window {
        audioPlayer: any;
    }
}

window.audioPlayer = (function () {
    let DeviceId = "default";
    let _isUnlocked = false;

    /*
     * AudioTrack - An individual audio file.
     */
    class AudioTrack implements IAudioTrack {
        private sound: Howl;
        private trackOptions: AudioTrackOptions;
        private playbackTimer: any;
        public isLoop: boolean;
        public isPrimary: boolean;
        public isPlaybackEnded: boolean = false;
        public isPlaybackCancelled: boolean = false;
        public onPlaybackEnded: (track: IAudioTrack) => void = () => {};

        constructor(trackOptions: AudioTrackOptions, isPrimary = false) {
            this.trackOptions = Object.assign({
                src: null,
                volume: 1,
                loop: false,
                offset: 0,
                seekTo: 0,
                duration: 0
            }, trackOptions);

            this.sound = new Howl({
                src: this.trackOptions.src ? [this.trackOptions.src] : [],
                volume: this.trackOptions.volume,
                loop: this.trackOptions.loop,
                format: ['mp3'],
            });

            this.isLoop = !!this.trackOptions.loop;
            this.isPrimary = isPrimary;

            this.sound.on('end', () => {
                if (this.trackOptions.loop || this.isPlaybackEnded) return;
                clearTimeout(this.playbackTimer);
                this.isPlaybackEnded = true;
                this.onPlaybackEnded(this);
            });
        }

        async play(): Promise<void> {
            if (this.trackOptions.offset && this.trackOptions.offset > 0) {
                await new Promise<void>(resolve => setTimeout(resolve, this.trackOptions.offset));
                if (this.isPlaybackCancelled) return;
            }
            await this._play();
        }

        private async _play(): Promise<void> {
            if (this.trackOptions.duration && this.trackOptions.duration > 0) {
                this.playbackTimer = setTimeout(() => {
                    this.stop();
                }, this.trackOptions.duration);
            }
            if (this.trackOptions.seekTo && this.trackOptions.seekTo > 0) {
                this.sound.seek(this.trackOptions.seekTo / 1000);
            }
            this.sound.play();
        }

        stop(): void {
            if (!this.isPlaybackEnded) {
                this.sound.stop();
                this.isPlaybackCancelled = true;
                this.isPlaybackEnded = true;
                this.onPlaybackEnded(this);
            }
        }

        pause(): void {
            this.sound.pause();
        }

        resume(): void {
            if (!this.isPlaybackEnded && !this.isPlaybackCancelled) {
                this.sound.play();
            }
        }
    }

    /*
     * AudioSection - A grouping of AudioTrack's that are played concurrently. 
     */
    class AudioSection implements IAudioSection {
        private tracks: AudioTrack[] = [];
        private sectionOptions: AudioSectionOptions;
        private endMode: EndModeType;
        private events: Required<AudioSectionOptions>['events'];

        constructor(sectionOptions: AudioSectionOptions) {
            this.sectionOptions = Object.assign({
                primaryTrack: {},
                secondaryTracks: [],
                endMode: EndMode.PrimaryTrackEnds,
                events: {
                    onPlaybackEnded: () => {},
                    onLastTrackEnded: () => {},
                    onPrimaryTrackEnded: () => {},
                    onPlaybackStarted: () => {}
                }
            }, sectionOptions);

            this.endMode = this.sectionOptions.endMode ?? EndMode.PrimaryTrackEnds;
            this.events = Object.assign({
                onPlaybackEnded: () => {},
                onLastTrackEnded: () => {},
                onPrimaryTrackEnded: () => {},
                onPlaybackStarted: () => {}
            }, this.sectionOptions.events);

            if (this.sectionOptions.primaryTrack) {
                this.tracks.push(new AudioTrack(this.sectionOptions.primaryTrack, true));
            }
            (this.sectionOptions.secondaryTracks || []).forEach(e => this.tracks.push(new AudioTrack(e, false)));
        }

        private isAnyTrackPlaying(): boolean {
            return this.tracks.some(e => !e.isLoop && !e.isPlaybackEnded);
        }

        async play(): Promise<void> {
            return new Promise<void>((res) => {
                this.tracks.forEach(track => {
                    track.onPlaybackEnded = onTrackPlaybackEnded;
                    track.play();
                });

                this.events.onPlaybackStarted();

                const self = this;
                function onTrackPlaybackEnded(ctx: IAudioTrack) {
                    if (ctx.isPrimary) {
                        self.events.onPrimaryTrackEnded();
                    }
                    if (!self.isAnyTrackPlaying()) {
                        self.events.onLastTrackEnded();
                    }
                    switch (self.endMode) {
                        case EndMode.PrimaryTrackEnds:
                            if (ctx.isPrimary) res();
                            break;
                        case EndMode.LastTrackEnds:
                        case EndMode.ExplicitStopRequest:
                            if (!self.isAnyTrackPlaying()) res();
                            break;
                    }
                }
            }).then(() => {
                this.stop();
                this.events.onPlaybackEnded();
            });
        }

        stop(): void {
            this.tracks.forEach(e => e.stop());
        }

        pause(): void {
            this.tracks.forEach(e => e.pause());
        }

        resume(): void {
            this.tracks.forEach(e => e.resume());
        }
    }

    /*
     * AudioSequence - A sequence of AudioSection's to be played sequentially.  
     */
    class AudioSequence implements IAudioSequence {
        private audioSection: AudioSection | null = null;
        private isStopRequested = false;
        private isPaused = false;
        private currentSectionIndex = 0;
        private sections: AudioSection[] = [];

        async play(audioSequenceOptions: AudioSequenceOptions): Promise<void> {
            this.sections = audioSequenceOptions.map(opt => new AudioSection(opt));
            this.currentSectionIndex = 0;
            this.isStopRequested = false;
            this.isPaused = false;
            await this.playNext();
        }

        private async playNext(): Promise<void> {
            if (this.isStopRequested || this.currentSectionIndex >= this.sections.length) return;
            this.audioSection = this.sections[this.currentSectionIndex];
            await this.audioSection.play();
            this.currentSectionIndex++;
            if (!this.isStopRequested && this.currentSectionIndex < this.sections.length) {
                await this.playNext();
            }
        }

        stop(): void {
            this.isStopRequested = true;
            if (this.audioSection) this.audioSection.stop();
        }

        pause(): void {
            this.isPaused = true;
            if (this.audioSection) this.audioSection.pause();
        }

        resume(): void {
            this.isPaused = false;
            if (this.audioSection) this.audioSection.resume();
        }
    }

    const playingAudioSequences: Record<string, AudioSequence> = {};

    const play = async (id: string, audioSequenceOptions: AudioSequenceOptions) => {
        playingAudioSequences[id] = new AudioSequence();
        const promise = playingAudioSequences[id].play(audioSequenceOptions);
        promise.then(() => {
            delete playingAudioSequences[id];
        });
        await promise;
    };

    const stop = (id: string) => {
        if (playingAudioSequences.hasOwnProperty(id)) {
            playingAudioSequences[id].stop();
        }
    };

    const pause = (id: string) => {
        if (playingAudioSequences.hasOwnProperty(id)) {
            playingAudioSequences[id].pause();
        }
    };

    const resume = (id: string) => {
        if (playingAudioSequences.hasOwnProperty(id)) {
            playingAudioSequences[id].resume();
        }
    };

    const unlock = async () => {
        return new Promise<void>(r => {
            if (_isUnlocked) return r();

            let hasResolved = false;
            let resolve = () => {
                r();
                hasResolved = true;
            };

            const sound = new Howl({
                src: 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU2LjM2LjEwMAAAAAAAAAAAAAAA//OEAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAAEAAABIADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV6urq6urq6urq6urq6urq6urq6urq6urq6v////////////////////////////////8AAAAATGF2YzU2LjQxAAAAAAAAAAAAAAAAJAAAAAAAAAAAASDs90hvAAAAAAAAAAAAAAAAAAAA//MUZAAAAAGkAAAAAAAAA0gAAAAATEFN//MUZAMAAAGkAAAAAAAAA0gAAAAARTMu//MUZAYAAAGkAAAAAAAAA0gAAAAAOTku//MUZAkAAAGkAAAAAAAAA0gAAAAANVVV',
                format: ['mp3'],
                onend: function () {
                    _isUnlocked = true;
                    resolve();
                }
            });

            sound.on('playerror', function () {
                _isUnlocked = false;
                resolve();
                sound.once('unlock', function () {
                    _isUnlocked = true;
                });
            });

            sound.play();
            setTimeout(() => {
                if (!hasResolved)
                    resolve();
            }, 250);
        });
    };

    const setAudioOutputDevice = async (deviceId: string) => {
        if (DeviceId === deviceId) return;

        // // https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/selectAudioOutput
        // // Only supported in Firefox
        // if (typeof(navigator.mediaDevices.selectAudioOutput) !== 'undefined') {
        //     await navigator.mediaDevices.selectAudioOutput({options: {deviceId: deviceId } });
        // }

        // https://developer.mozilla.org/en-US/docs/Web/API/AudioContext/setSinkId
        // Unsupported in Safari, Firefox.
        // @ts-expect-error: setSinkId is not standard on AudioContext, but supported in some browsers (e.g., Chrome)
        if (typeof (Howler.ctx.setSinkId) !== 'undefined') {
            // @ts-expect-error: setSinkId is not standard on AudioContext, but supported in some browsers (e.g., Chrome)
            await Howler.ctx.setSinkId(deviceId === "default" ? "" : deviceId);
        }
        DeviceId = deviceId;
    };

    const hasPlaybackPermissions = async (): Promise<boolean> => {
        return _isUnlocked;
    };

    return {
        play,
        stop,
        pause,
        resume,
        unlock,
        setAudioOutputDevice,
        hasPlaybackPermissions,
    };
})();
